import { useCallback } from "react";
import { toastManager } from "@salestrak/ui";
import { useIssueViewOptional } from "#app/components/issue-view/use-issue-view";
import { MovedToTrashTitle } from "#app/components/settings/moved-to-trash-title";
import { useTrashMutations } from "#app/hooks/settings/use-trash-mutations";
import { useRecentItems } from "#app/lib/hooks/use-recent-items";
import { UNDO_TOAST_SHORTCUT, UNDO_TOAST_TIMEOUT_MS } from "#app/lib/undo-shortcut";
import { useIssueCardActions } from "./use-issue-card-actions";
import type { IssueActionRef } from "./use-issue-actions";
import { AnalyticsContext, KanbanEvent, trackEvent } from "#app/lib/analytics";

export function useDeleteIssueWithUndo() {
  const { deleteIssue } = useIssueCardActions();
  const { restoreIssue } = useTrashMutations();
  const issueView = useIssueViewOptional();
  const { forgetVisit } = useRecentItems();

  return useCallback(
    async (issue: IssueActionRef) => {
      if (!(await deleteIssue(issue))) return;
      // A trashed project is not left open behind the toast, still taking
      // edits. Undo restores it; the viewer reopens it from where it lands.
      issueView?.closeIssueIfOpen(issue.issueId);
      // Nor is it left in Recent: reopened from there it would look like a
      // live Project. An undo puts it back on the viewer's next open.
      forgetVisit({ kind: "issue", id: issue.issueId });
      // The one sanctioned neutral action toast: no `type`, so it renders
      // without a status icon, and the undo affordance is the toast action —
      // pressed, or fired by ⌘Z while the toast stands.
      const toastId = toastManager.add({
        title: <MovedToTrashTitle subject="Project" />,
        actionProps: {
          children: "Undo",
          onClick: () => {
            // The self-serve restore (delete.items), NOT restoreTrashItem:
            // whoever this delete worked for, the undo must work for too —
            // trash.manage would refuse most of the people who see the toast.
            void restoreIssue(issue.issueId).then((ok) => {
              if (!ok) return;
              trackEvent({
                context: AnalyticsContext.Kanban,
                event: KanbanEvent.Restore,
                data: { via: "undo_toast" },
              });
            });
            // Toast.Action does not close its toast; an offer that was taken
            // must leave with the press.
            toastManager.close(toastId);
          },
        },
        data: { actionShortcut: UNDO_TOAST_SHORTCUT },
        timeout: UNDO_TOAST_TIMEOUT_MS,
      });
    },
    [deleteIssue, forgetVisit, issueView, restoreIssue],
  );
}
